class InstafeedVideos extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVideoPlay = this.handleVideoPlay.bind(this);
    this.updateModalSizing = this.updateModalSizing.bind(this);
    this.updateFeedSlider = this.updateFeedSlider.bind(this);
    this.activeIndex = 0;
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('play', this.handleVideoPlay, true);
    this.modal = this.querySelector('.instafeed-videos__modal');
    this.modalDialog = this.querySelector('.instafeed-videos__modal-dialog');
    this.modalStage = this.querySelector('[data-instafeed-modal-stage]');
    window.addEventListener('resize', this.updateFeedSlider);
    // this.queueFeedSliderUpdate();
    // this.playAutoplayFeedVideo();

    this.queueFeedSliderUpdate();

    const loadAutoplayVideo = () => this.playAutoplayFeedVideo();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadAutoplayVideo, { timeout: 3000 });
    } else {
      setTimeout(loadAutoplayVideo, 2000);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('play', this.handleVideoPlay, true);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.updateFeedSlider);
    window.removeEventListener('resize', this.updateModalSizing);
    window.visualViewport?.removeEventListener('resize', this.updateModalSizing);
    document.documentElement.classList.remove('instafeed-modal-open');
    this.pauseFeedVideos();
  }

  queueFeedSliderUpdate() {
    requestAnimationFrame(this.updateFeedSlider);
    setTimeout(this.updateFeedSlider, 250);
    setTimeout(this.updateFeedSlider, 900);
  }

  updateFeedSlider() {
    const swiper = this.querySelector('.instafeed-videos__slider')?.swiper;
    if (!swiper || swiper.destroyed) {
      return;
    }

    swiper.update();
  }

  handleClick(event) {
    const button = event.target.closest('.instafeed-video-card__mute');

    if (button && this.contains(button)) {
      this.toggleSound(button);
      return;
    }

    const volumeButton = event.target.closest('[data-instafeed-volume-toggle]');

    if (volumeButton && this.contains(volumeButton)) {
      this.toggleModalVolume(volumeButton);
      return;
    }

    if (event.target.closest('[data-instafeed-close]')) {
      this.closeModal();
      return;
    }

    if (event.target.closest('[data-instafeed-prev]')) {
      this.showModalCard(this.activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-instafeed-next]')) {
      this.showModalCard(this.activeIndex + 1);
      return;
    }

    const productButton = event.target.closest('.instafeed-video-card__button');
    const buttonCard = productButton ? productButton.closest('[data-instafeed-card]') : null;

    if (buttonCard && this.contains(buttonCard) && !buttonCard.closest('.instafeed-videos__modal')) {
      event.preventDefault();
      this.openModal(Number(buttonCard.dataset.instafeedIndex || 0));
      return;
    }

    if (event.target.closest('a, button, input, select, textarea, video[controls]')) {
      return;
    }

    const card = event.target.closest('[data-instafeed-card]');

    if (card && this.contains(card) && !card.closest('.instafeed-videos__modal')) {
      this.openModal(Number(card.dataset.instafeedIndex || 0));
    }
  }

  toggleSound(button) {
    const card = button.closest('.instafeed-video-card');
    const videos = card ? [...card.querySelectorAll('video')] : [];
    const activeVideo = videos.find(video => this.isVisible(video)) || videos[0];
    const isMuted = button.getAttribute('aria-pressed') !== 'true';
    const nextMuted = !isMuted;

    videos.forEach(video => {
      video.muted = nextMuted;

      if (video === activeVideo && !nextMuted) {
        video.volume = 1;
        video.play().catch(() => {});
      } else if (video !== activeVideo) {
        video.pause();
      }
    });

    button.setAttribute('aria-pressed', nextMuted ? 'false' : 'true');

    const label = button.querySelector('[data-instafeed-mute-label]');
    if (label) {
      label.textContent = nextMuted ? 'Sound on' : 'Mute';
    }
  }

  openModal(index) {
    if (!this.modal || !this.modalStage) {
      return;
    }

    this.modal.hidden = false;
    document.documentElement.classList.add('instafeed-modal-open');
    this.pauseFeedVideos();
    this.updateModalSizing();
    this.showModalCard(index);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.updateModalSizing);
    window.visualViewport?.addEventListener('resize', this.updateModalSizing);

    window.requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
      this.modalDialog?.focus();
    });
  }

  closeModal() {
    if (!this.modal || this.modal.hidden) {
      return;
    }

    this.modal.classList.remove('is-open');
    this.modalStage.innerHTML = '';
    this.modal.hidden = true;
    document.documentElement.classList.remove('instafeed-modal-open');
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.updateModalSizing);
    window.visualViewport?.removeEventListener('resize', this.updateModalSizing);
    this.playAutoplayFeedVideo();
  }

  updateModalSizing() {
    if (!this.modal) {
      return;
    }

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const outerWidth = window.outerWidth || viewportWidth;
    const zoomRatio = outerWidth > 0 ? Math.max(1, viewportWidth / outerWidth) : 1;
    const maxWidth = Math.min(viewportWidth * 0.9, viewportHeight * 0.9 * (9 / 16), 480 * zoomRatio);

    this.modal.style.setProperty('--instafeed-modal-js-max-width', `${Math.max(280, maxWidth)}px`);
  }

  showModalCard(index) {
    const cards = [...this.querySelectorAll('.instafeed-videos__slider [data-instafeed-card]')];

    if (!cards.length || !this.modalStage) {
      return;
    }

    this.activeIndex = (index + cards.length) % cards.length;
    const card = cards[this.activeIndex].cloneNode(true);
    card.classList.add('instafeed-video-card--modal');
    card.removeAttribute('data-instafeed-card');

    this.modalStage.innerHTML = '';
    this.modalStage.appendChild(card);

    const videos = [...card.querySelectorAll('video')];
    const activeVideo = videos.find(video => this.isVisible(video)) || videos[0];

    videos.forEach(video => {
      video.muted = true;
      video.controls = false;
      video.volume = 1;
      video.preload = video === activeVideo ? 'auto' : 'none';

      if (video === activeVideo) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    const muteButton = card.querySelector('.instafeed-video-card__mute');
    if (muteButton) {
      muteButton.remove();
    }

    this.addModalVolumeControl(card);
    this.prepareModalProducts(card);
    card.querySelector('.instafeed-video-card__button')?.remove();
  }

  addModalVolumeControl(card) {
    if (card.querySelector('[data-instafeed-volume]')) {
      return;
    }

    const volumeControl = document.createElement('div');
    volumeControl.className = 'instafeed-video-card__volume';
    volumeControl.setAttribute('data-instafeed-volume', '');
    volumeControl.innerHTML = `
      <button class="instafeed-video-card__volume-toggle is-muted" type="button" data-instafeed-volume-toggle aria-pressed="true">
        <span class="visually-hidden" data-instafeed-volume-label>Turn sound on</span>
        <svg class="instafeed-video-card__volume-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path class="instafeed-video-card__volume-speaker" d="M4 9.5H8L13 5.5V18.5L8 14.5H4V9.5Z" />
          <path class="instafeed-video-card__volume-wave" d="M16 9C17.2 10.2 17.2 13.8 16 15" />
          <path class="instafeed-video-card__volume-slash" d="M17.5 7L7 17.5" />
        </svg>
      </button>
    `;

    card.appendChild(volumeControl);
    this.updateModalVolumeControl(card);
  }

  toggleModalVolume(button) {
    const card = button.closest('.instafeed-video-card');
    const videos = card ? [...card.querySelectorAll('video')] : [];
    const activeVideo = videos.find(video => this.isVisible(video)) || videos[0];
    const shouldMute = button.getAttribute('aria-pressed') !== 'true';

    videos.forEach(video => {
      video.muted = shouldMute;

      if (video === activeVideo) {
        video.volume = 1;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    this.updateModalVolumeControl(card);
  }

  updateModalVolumeControl(card) {
    if (!card) {
      return;
    }

    const video = card.querySelector('video');
    const button = card.querySelector('[data-instafeed-volume-toggle]');
    const label = card.querySelector('[data-instafeed-volume-label]');

    if (!video || !button) {
      return;
    }

    const isMuted = video.muted || video.volume === 0;

    button.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
    button.classList.toggle('is-muted', isMuted);

    if (label) {
      label.textContent = isMuted ? 'Turn sound on' : 'Mute video';
    }
  }

  prepareModalProducts(card) {
    const overlay = card.querySelector('.instafeed-video-card__overlay');

    if (!overlay) {
      return;
    }

    const productCard = overlay.querySelector('.instafeed-video-card__product-card');
    const moreProducts = overlay.querySelector('.instafeed-video-card__more-products');
    const moreItems = moreProducts ? [...moreProducts.querySelectorAll('.instafeed-video-card__more-item')] : [];

    overlay.querySelector('.instafeed-video-card__more-heading')?.remove();
    overlay.querySelector('.instafeed-video-card__button')?.remove();
    overlay.innerHTML = '';

    if (productCard) {
      overlay.appendChild(productCard);
    }

    moreItems.forEach(item => {
      overlay.appendChild(item);
    });
  }

  pauseFeedVideos() {
    this.querySelectorAll('.instafeed-videos__slider video').forEach(video => video.pause());
  }

  handleVideoPlay(event) {
    const activeVideo = event.target;

    if (!activeVideo?.matches?.('.instafeed-videos__slider video')) {
      return;
    }

    if (!this.isVisible(activeVideo)) {
      activeVideo.pause();
      return;
    }

    this.querySelectorAll('.instafeed-videos__slider video').forEach(video => {
      if (video !== activeVideo) {
        video.pause();
      }
    });
  }

  playAutoplayFeedVideo() {
    const autoplayCard = this.querySelector('.instafeed-videos__slider [data-instafeed-autoplay-card]');
    const videos = [...this.querySelectorAll('.instafeed-videos__slider video')];
    const activeVideo = autoplayCard ? [...autoplayCard.querySelectorAll('video')].find(video => this.isVisible(video)) : null;

    videos.forEach(video => {
      video.preload = video === activeVideo ? 'auto' : 'none';

      if (video !== activeVideo) {
        video.pause();
      }
    });

    if (activeVideo) {
      activeVideo.muted = true;
      activeVideo.play().catch(() => {});
    }
  }

  isVisible(element) {
    if (!element || !element.getClientRects().length) {
      return false;
    }

    const styles = window.getComputedStyle(element);
    return styles.visibility !== 'hidden' && styles.display !== 'none';
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    } else if (event.key === 'ArrowLeft') {
      this.showModalCard(this.activeIndex - 1);
    } else if (event.key === 'ArrowRight') {
      this.showModalCard(this.activeIndex + 1);
    }
  }
}

if (!customElements.get('instafeed-videos')) {
  customElements.define('instafeed-videos', InstafeedVideos);
}
